import { effect, frame } from './move-types'
import type { Move } from './move-types'

export const signalMoves = {
  double_nod: {
    label: 'Double nod',
    duration: 1.9,
    expression: 'reassuring',
    description:
      'Give one clear yes nod, then a smaller follow-up nod, and return to a warm smile.',
    frames: [
      frame(0),
      frame(0.15, { pitch: -0.055 }),
      frame(0.32, { pitch: 0.2, stretch: -0.025 }),
      frame(0.48, { pitch: -0.06 }),
      frame(0.67, { pitch: 0.13, stretch: -0.015 }),
      frame(0.83, { pitch: -0.025 }),
      frame(1),
    ],
  },
  shy_swivel: {
    label: 'Shy swivel',
    duration: 2.1,
    expression: 'reassuring',
    description:
      'Grow a little taller, turn bashfully to one side, peek back, and unwind to face you.',
    frames: [
      frame(0),
      frame(0.2, { stretch: 0.055, roll: 0.04 }),
      frame(0.42, { yaw: 0.45, roll: 0.075, stretch: 0.1, x: 0.025 }),
      frame(0.62, { yaw: 0.35, roll: 0.045, stretch: 0.075 }),
      frame(0.82, { yaw: -0.09, roll: -0.025 }),
      frame(1),
    ],
  },
  magnetic_return: {
    label: 'Magnetic return',
    duration: 2,
    expression: 'relaxed',
    description: 'Drift away in a tilted arc, get pulled home, overshoot a little, then settle.',
    frames: [
      frame(0),
      frame(0.25, { x: -0.11, y: 0.065, roll: 0.18, yaw: -0.13 }),
      frame(0.42, { x: -0.12, y: 0.085, roll: 0.21, yaw: -0.15 }),
      frame(0.63, { x: 0.055, y: 0.02, roll: -0.1, stretch: -0.055 }),
      frame(0.79, { x: -0.018, roll: 0.04, stretch: 0.025 }),
      frame(1),
    ],
  },
  gentle_recoil: {
    label: 'Gentle recoil',
    expressions: [
      [0, 'attentive'],
      [0.12, 'surprised'],
      [0.65, 'curious'],
    ],
    duration: 1.8,
    expression: 'surprised',
    description:
      'Pop back with a surprised face, lean away, then cautiously spring forward to rest.',
    frames: [
      frame(0),
      frame(0.18, { z: -0.09, x: 0.055, pitch: -0.16, roll: 0.12, stretch: 0.045 }),
      frame(0.38, { z: -0.075, x: 0.05, pitch: -0.12, roll: 0.09 }),
      frame(0.65, { z: 0.025, x: -0.015, pitch: 0.065, stretch: -0.05 }),
      frame(0.83, { pitch: -0.025, stretch: 0.025 }),
      frame(1),
    ],
  },
  eureka: {
    label: 'Eureka',
    duration: 2.2,
    expression: 'attentive',
    description:
      'Notice an idea, rise as light climbs the seam, pop a violet star, then settle with a smile.',
    expressions: [
      [0, 'attentive'],
      [0.3, 'relaxed'],
      [0.48, 'delighted'],
      [0.86, 'relaxed'],
    ],
    frames: [
      frame(0),
      frame(0.16, { stretch: -0.045, pitch: -0.035 }),
      frame(0.38, { stretch: 0.1, y: 0.035, roll: -0.035 }),
      frame(0.56, { stretch: 0.035, y: 0.045, roll: 0.035 }),
      frame(0.78, { stretch: -0.025 }),
      frame(1),
    ],
    effects: [
      effect(0),
      effect(0.16, { trailPosition: 0.25 }),
      effect(0.38, { trail: 1, trailPosition: 0.09 }),
      effect(0.52, { star: 1, trail: 1, trailPosition: -0.04 }),
      effect(0.65, { star: 0.8, trail: 0.4, trailPosition: -0.15 }),
      effect(0.84, { trailPosition: -0.25 }),
      effect(1),
    ],
  },
  happy_landing: {
    label: 'Happy landing',
    duration: 2.3,
    expression: 'delighted',
    description:
      'Squash in anticipation, hop through a playful turn, land with two violet ripples, and beam.',
    expressions: [
      [0, 'delighted'],
      [0.22, 'relaxed'],
      [0.56, 'delighted'],
    ],
    frames: [
      frame(0),
      frame(0.15, { stretch: -0.16, yaw: -0.18 }),
      frame(0.32, { y: 0.14, yaw: 0.7, roll: -0.07, stretch: 0.06 }),
      frame(0.45, { y: 0.13, yaw: -0.18, roll: 0.06 }),
      frame(0.58, { stretch: -0.2 }),
      frame(0.72, { stretch: 0.035 }),
      frame(0.87, { stretch: -0.015 }),
      frame(1),
    ],
    effects: [
      effect(0),
      effect(0.58),
      effect(0.62, { ripple: 0.08, rippleOpacity: 1 }),
      effect(0.72, { ripple: 0.45, rippleOpacity: 0.75 }),
      effect(0.96, { ripple: 1 }),
      effect(1, { ripple: 1 }),
    ],
  },
  generating_spin: {
    label: 'Generating spin',
    duration: 2,
    expression: 'delighted',
    description:
      'Turn a full circle on the vertical axis in one second, then rest for a second before repeating.',
    frames: [
      frame(0),
      frame(0.12, { yaw: 1.15 }),
      frame(0.26, { yaw: 3.05 }),
      frame(0.4, { yaw: 5.05 }),
      frame(0.5, { yaw: Math.PI * 2 }),
      frame(0.75, { yaw: Math.PI * 2 }),
      frame(1, { yaw: Math.PI * 2 }),
    ],
  },
  message_received: {
    label: 'Message received',
    duration: 2.1,
    expression: 'attentive',
    description:
      'Listen, give a warm nod, send two light pulses along the seam, and return to ready.',
    expressions: [
      [0, 'attentive'],
      [0.24, 'delighted'],
      [0.82, 'relaxed'],
    ],
    frames: [
      frame(0),
      frame(0.16, { pitch: -0.055, roll: -0.025 }),
      frame(0.37, { pitch: 0.22, stretch: -0.05 }),
      frame(0.56, { pitch: 0.13, stretch: -0.025 }),
      frame(0.76, { pitch: -0.035 }),
      frame(1),
    ],
    effects: [
      effect(0),
      effect(0.32, { trailPosition: 0.12 }),
      effect(0.42, { trail: 1, trailPosition: 0.02 }),
      effect(0.52, { trailPosition: -0.06 }),
      effect(0.6, { trail: 1, trailPosition: -0.1 }),
      effect(0.78, { trailPosition: -0.25 }),
      effect(1),
    ],
  },
} satisfies Record<string, Move>
